import { useEffect, useState } from 'react';
import { api, type Field, type Meta, type PreviewResult, type Source } from '../api';
import { Button, Copy, Dot, Empty, ErrorNote, Field as FormField, Input, Panel, Select, healthOf, relativeTime } from './ui';
import { PreviewPanel, type Pick } from './PreviewPanel';
import { FieldEditor, draftFrom, emptyDraft, type DraftField } from './FieldEditor';
import { LoxonePanel } from './LoxonePanel';

/** Everything about one source: its settings, its fields, and its Loxone handoff. */
export function SourceWorkbench({
  source,
  meta,
  canEdit,
  onChanged,
  onDeleted,
}: {
  source: Source;
  meta: Meta;
  canEdit: boolean;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [tab, setTab] = useState<'fields' | 'settings' | 'loxone'>('fields');
  const [draft, setDraft] = useState<DraftField | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewPending, setPreviewPending] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [planKey, setPlanKey] = useState(0);

  // A different source means a different payload; keep nothing from the last one.
  useEffect(() => {
    setDraft(null);
    setPreview(null);
    setError(null);
  }, [source.id]);

  async function fetchPreview(): Promise<void> {
    setPreviewPending(true);
    setError(null);
    try {
      setPreview(
        await api.preview(
          source.mode === 'push'
            ? { payload: source.last_payload ?? '', kind: source.kind, timezone: source.timezone }
            : {
                url: source.url ?? '',
                kind: source.kind,
                method: source.method,
                headers: source.headers,
                timezone: source.timezone,
              }
        )
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewPending(false);
    }
  }

  async function runNow(): Promise<void> {
    setRunning(true);
    setError(null);
    try {
      const result = await api.runSource(source.id);
      if (!result.ok) setError(result.error ?? 'run failed');
      onChanged();
      setPlanKey((k) => k + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
    }
  }

  /** Clicking a value in the preview writes its expression into the field being edited. */
  function applyPick(pick: Pick): void {
    setDraft((current) => {
      const base = current ?? emptyDraft();
      return {
        ...base,
        // A series field keeps its own extractor; picking only fills the values path.
        extractor: base.extractor === 'series' ? 'series' : pick.extractor,
        expr: pick.expr,
        ...(pick.attr ? { attr: pick.attr } : {}),
        // Suggest a key from the path so the common case needs no typing.
        key: base.key || pick.expr.split(/[.>\s]/).pop()?.replace(/[^A-Za-z0-9_]/g, '') || '',
      };
    });
  }

  const health = healthOf(source);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Dot health={health} />
            <h1 className="text-lg font-medium truncate">{source.name}</h1>
            <span className="mono text-xs text-ink-500">{source.slug}</span>
          </div>
          <p className="mono text-xs text-ink-500 truncate mt-1">
            {source.mode === 'push' ? 'receives data by webhook' : source.url}
          </p>
          <p className="text-xs text-ink-500 mt-1">
            every {source.interval_seconds}s · last success {relativeTime(source.last_success_at)}
            {source.last_duration_ms !== null && ` · ${source.last_duration_ms}ms`}
            {source.consecutive_failures > 0 && (
              <span className="text-bad"> · {source.consecutive_failures} consecutive failure(s)</span>
            )}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {canEdit && (
            <Button onClick={() => void runNow()} disabled={running}>
              {running ? 'Running…' : 'Run now'}
            </Button>
          )}
        </div>
      </div>

      {source.last_error && <ErrorNote>{source.last_error}</ErrorNote>}
      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="flex gap-1 border-b border-ink-800">
        {(['fields', 'settings', 'loxone'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
              tab === t ? 'border-accent text-ink-100' : 'border-transparent text-ink-500 hover:text-ink-300'
            }`}
          >
            {t === 'loxone' ? 'Loxone' : t[0]!.toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'fields' && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
          <div className="space-y-4">
            <Panel
              title="Fields"
              actions={
                canEdit && (
                  <Button onClick={() => setDraft(emptyDraft())} variant="primary">
                    New field
                  </Button>
                )
              }
            >
              {source.fields.length === 0 ? (
                <Empty>No fields yet. Fetch the payload, then click a value to pick it.</Empty>
              ) : (
                <div className="divide-y divide-ink-800 -m-4">
                  {source.fields.map((f: Field) => (
                    <button
                      key={f.id}
                      onClick={() => setDraft(draftFrom(f))}
                      className={`w-full text-left px-4 py-2 hover:bg-ink-850 flex items-center gap-3 ${
                        draft?.id === f.id ? 'bg-ink-850' : ''
                      }`}
                    >
                      <span
                        className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${
                          f.last_ok === false ? 'bg-bad' : f.value !== null ? 'bg-good' : 'bg-ink-700'
                        }`}
                      />
                      <span className="mono text-xs text-ink-100 truncate w-36">{f.key}</span>
                      <span className="mono text-sm text-ink-300 truncate flex-1 text-right">
                        {f.last_ok === false ? (
                          <span className="text-bad text-xs">{f.last_error}</span>
                        ) : (
                          <>
                            {f.value ?? '—'} <span className="text-ink-500 text-xs">{f.unit}</span>
                          </>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </Panel>

            <Panel
              title={source.mode === 'push' ? 'Last received payload' : 'Payload'}
              actions={
                <Button onClick={() => void fetchPreview()} disabled={previewPending}>
                  {previewPending ? 'Loading…' : source.mode === 'push' ? 'Show' : 'Fetch'}
                </Button>
              }
            >
              <PreviewPanel result={preview} kind={source.kind} onPick={applyPick} pending={previewPending} />
            </Panel>
          </div>

          <div className="xl:sticky xl:top-4">
            {draft ? (
              <Panel title={draft.id ? `Edit ${draft.key || 'field'}` : 'New field'}>
                <FieldEditor
                  source={source}
                  meta={meta}
                  draft={draft}
                  canEdit={canEdit}
                  onDraftChange={setDraft}
                  onSaved={() => {
                    setDraft(null);
                    onChanged();
                    setPlanKey((k) => k + 1);
                  }}
                  onCancel={() => setDraft(null)}
                />
                {draft.id && canEdit && (
                  <div className="mt-4 pt-4 border-t border-ink-800">
                    <Button
                      variant="danger"
                      onClick={() => {
                        if (!confirm(`Delete field "${draft.key}"?`)) return;
                        void api.deleteField(draft.id!).then(() => {
                          setDraft(null);
                          onChanged();
                          setPlanKey((k) => k + 1);
                        });
                      }}
                    >
                      Delete field
                    </Button>
                  </div>
                )}
              </Panel>
            ) : (
              <Panel title="Field editor">
                <Empty>Select a field, or pick a value from the payload to start a new one.</Empty>
              </Panel>
            )}
          </div>
        </div>
      )}

      {tab === 'settings' && (
        <SourceSettings source={source} meta={meta} canEdit={canEdit} onChanged={onChanged} onDeleted={onDeleted} />
      )}

      {tab === 'loxone' && (
        <Panel title="Get this into Loxone Config">
          <LoxonePanel sourceId={source.id} refreshKey={planKey} />
        </Panel>
      )}
    </div>
  );
}

const origin = typeof window === 'undefined' ? '' : window.location.origin;

function SourceSettings({
  source,
  meta,
  canEdit,
  onChanged,
  onDeleted,
}: {
  source: Source;
  meta: Meta;
  canEdit: boolean;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [form, setForm] = useState({ ...source, headersText: JSON.stringify(source.headers, null, 2) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm({ ...source, headersText: JSON.stringify(source.headers, null, 2) });
  }, [source]);

  async function save(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      let headers: Record<string, string> = {};
      try {
        headers = JSON.parse(form.headersText || '{}');
      } catch {
        throw new Error('headers must be valid JSON');
      }
      await api.updateSource(source.id, {
        name: form.name,
        slug: form.slug,
        url: form.url,
        kind: form.kind,
        method: form.method,
        timezone: form.timezone,
        interval_seconds: Number(form.interval_seconds),
        enabled: form.enabled,
        notes: form.notes,
        headers,
      });
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]): void => setForm({ ...form, [k]: v });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
      <Panel title="Settings">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Name">
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} disabled={!canEdit} />
            </FormField>
            <FormField label="Slug" hint="appears in the /out URL">
              <Input mono value={form.slug} onChange={(e) => set('slug', e.target.value)} disabled={!canEdit} />
            </FormField>
          </div>

          <FormField label="URL">
            <Input
              mono
              value={form.url ?? ''}
              onChange={(e) => set('url', e.target.value)}
              disabled={!canEdit || source.mode === 'push'}
              placeholder={source.mode === 'push' ? 'not used — data is pushed here' : ''}
            />
          </FormField>

          <div className="grid grid-cols-3 gap-3">
            <FormField label="Format">
              <Select value={form.kind} onChange={(e) => set('kind', e.target.value as Source['kind'])} disabled={!canEdit}>
                {meta.sourceKinds.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Interval (s)" hint={`minimum ${meta.minIntervalSeconds}`}>
              <Input
                type="number"
                value={form.interval_seconds}
                onChange={(e) => set('interval_seconds', Number(e.target.value))}
                disabled={!canEdit}
              />
            </FormField>
            <FormField label="Timezone" hint="for timestamps with no offset">
              <Input
                mono
                value={form.timezone}
                placeholder="Europe/Zagreb"
                onChange={(e) => set('timezone', e.target.value)}
                disabled={!canEdit}
              />
            </FormField>
          </div>

          <FormField label="Request headers" hint="JSON object, e.g. an API key">
            <textarea
              value={form.headersText}
              onChange={(e) => set('headersText', e.target.value)}
              disabled={!canEdit}
              rows={3}
              className="mono w-full bg-ink-900 border border-ink-800 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:border-accent/70"
            />
          </FormField>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => set('enabled', e.target.checked)}
              disabled={!canEdit}
            />
            <span>Polling enabled</span>
          </label>

          {error && <ErrorNote>{error}</ErrorNote>}

          {canEdit && (
            <div className="flex gap-2 pt-1">
              <Button variant="primary" onClick={() => void save()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          )}
        </div>
      </Panel>

      <div className="space-y-4">
        {source.mode === 'push' && source.in_token && (
          <Panel title="Where to send data">
            <p className="text-sm text-ink-300 mb-2">
              Have the other system POST JSON here. A device that can only issue a GET may put
              its values in the query string instead.
            </p>
            <div className="flex items-center gap-2">
              <code className="mono text-xs text-ink-300 bg-ink-950 border border-ink-800 rounded px-2 py-1 truncate flex-1">
                {origin}/in/{source.slug}/{source.in_token}
              </code>
              <Copy text={`${origin}/in/${source.slug}/${source.in_token}`} />
            </div>
            <p className="text-xs text-ink-500 mt-2">
              This token writes. The one below only reads — they are deliberately different, so
              being able to see a source never means being able to forge it.
            </p>
          </Panel>
        )}

        <Panel title="Access token">
          <p className="text-sm text-ink-300 mb-2">
            This token is what authorises a Miniserver to read this source, and only this source. Rotating it
            immediately invalidates the old URL, so any Loxone input using it must be updated.
          </p>
          <div className="flex items-center gap-2">
            <code className="mono text-xs text-ink-300 bg-ink-950 border border-ink-800 rounded px-2 py-1 truncate flex-1">
              {source.out_token}
            </code>
            <Copy text={source.out_token} />
          </div>
          {canEdit && (
            <div className="mt-3">
              <Button
                variant="danger"
                onClick={() => {
                  if (!confirm('Rotate the token? Every Loxone input using the current URL will stop working until updated.')) return;
                  void api.rotateToken(source.id).then(onChanged);
                }}
              >
                Rotate token
              </Button>
            </div>
          )}
        </Panel>

        {canEdit && (
          <Panel title="Danger zone">
            <Button
              variant="danger"
              onClick={() => {
                if (!confirm(`Delete "${source.name}" and all its fields? This cannot be undone.`)) return;
                void api.deleteSource(source.id).then(onDeleted);
              }}
            >
              Delete this source
            </Button>
          </Panel>
        )}
      </div>
    </div>
  );
}
